import {
  DocumentContent,
  DocumentMediaType,
  ImageContent,
  ImageMediaType,
} from '../types';

export const MAX_ATTACHMENTS = 5;
export const MAX_IMAGE_SIZE_MB = 5;
export const MAX_DOCUMENT_SIZE_MB = 20;

export const SUPPORTED_IMAGE_TYPES: ImageMediaType[] = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
];

// This should be a full list (partial not needed) but it will protect us when Fabry starts supporting new formats.
export const IMAGE_TYPE_CONFIG: Partial<
  Record<ImageMediaType, { label: string; color: string }>
> = {
  'image/jpeg': { label: 'JPEG', color: 'warning.main' },
  'image/png': { label: 'PNG', color: 'warning.main' },
  'image/gif': { label: 'GIF', color: 'warning.main' },
  'image/webp': { label: 'WEBP', color: 'warning.main' },
};

export const IMAGE_TYPE_FALLBACK = { label: 'IMG', color: 'warning.main' };

// This should be a full list (partial not needed) but it will protect us when Fabry starts supporting new formats.
export const DOCUMENT_TYPE_CONFIG: Partial<
  Record<DocumentMediaType, { label: string; color: string; extension: string }>
> = {
  'application/pdf': { label: 'PDF', color: 'error.main', extension: '.pdf' },
  'text/csv': { label: 'CSV', color: 'success.main', extension: '.csv' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
    label: 'XLSX',
    color: 'success.main',
    extension: '.xlsx',
  },
  'application/vnd.ms-excel': {
    label: 'XLS',
    color: 'success.main',
    extension: '.xls',
  },
  'text/plain': { label: 'TXT', color: 'grey.500', extension: '.txt' },
  'text/markdown': {
    label: 'MD',
    color: 'info.main',
    extension: '.md',
  },
};

export const DOCUMENT_TYPE_FALLBACK = { label: 'FILE', color: 'grey.500' };

export const SUPPORTED_DOCUMENT_TYPES = Object.keys(DOCUMENT_TYPE_CONFIG);

export const SUPPORTED_FILE_TYPES = [
  ...SUPPORTED_IMAGE_TYPES,
  ...SUPPORTED_DOCUMENT_TYPES,
];

export const ACCEPTED_FILE_INPUT = [
  ...SUPPORTED_FILE_TYPES,
  ...Object.values(DOCUMENT_TYPE_CONFIG).map(c => c.extension),
].join(',');

export const isImageMediaType = (type: string): type is ImageMediaType =>
  SUPPORTED_IMAGE_TYPES.some(t => t === type);

export const isDocumentMediaType = (type: string): type is DocumentMediaType =>
  SUPPORTED_DOCUMENT_TYPES.some(t => t === type);

export const readFileAsBase64 = (file: File): Promise<string | null> => {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        resolve(null);
        return;
      }
      const base64Data = reader.result.split(',')[1];
      resolve(base64Data ?? null);
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
};

export const fileToImageContent = async (
  file: File
): Promise<ImageContent | null> => {
  if (!isImageMediaType(file.type)) return null;
  if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) return null;

  const base64Data = await readFileAsBase64(file);
  if (!base64Data) return null;

  return {
    type: 'image',
    media_type: file.type,
    data: base64Data,
  };
};

export const fileToDocumentContent = async (
  file: File
): Promise<DocumentContent | null> => {
  if (!isDocumentMediaType(file.type)) return null;
  if (file.size > MAX_DOCUMENT_SIZE_MB * 1024 * 1024) return null;

  const base64Data = await readFileAsBase64(file);
  if (!base64Data) return null;

  return {
    type: 'document',
    media_type: file.type,
    data: base64Data,
    filename: file.name,
  };
};
